import {
  ModalHeader
} from "@telegram-apps/telegram-ui/dist/components/Overlays/Modal/components/ModalHeader/ModalHeader";
import {Badge, Blockquote, Button, Image, Modal, Spinner} from "@telegram-apps/telegram-ui";
import {useEffect, useState} from "react";
import {Idea} from "@/api/types.ts";
import {
  disableVerticalSwipes,
  enableVerticalSwipes,
  mainButton,
  miniApp,
  useSignal
} from "@telegram-apps/sdk-react";
import {TbGift, TbWorldShare} from "react-icons/tb";
import {openExternalLink} from "@/helpers/openExternalLink.ts";
import {useTranslation} from "react-i18next";
import {renderWithHtml} from "@/helpers/renderWithHtml.tsx";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {setOnClickMainButton} from "@/helpers/tgButtons.ts";
import {applyIdea, hideIdea as callHideIdea} from "@/api/apiMethods.ts";
import {useOutletContext} from "react-router-dom";
import {BiHide} from "react-icons/bi";
import {datadogLogs} from "@datadog/browser-logs";
import {haptic} from "@/helpers/haptic.ts";

export const IdeaModal = (
  {idea, onClose }:
  { idea: Idea, onClose: () => void }
) => {
  const { t } = useTranslation();
  const { showSnackbar } = useNavigation();
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState<boolean>(true);
  const [addingInProgress, setAddingInProgress] = useState<boolean>(false);
  const isDark = useSignal(miniApp.isDark);
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
  const { onWishSaved } = useOutletContext<{onWishSaved: () => Promise<void> | undefined}>();

  const bottomSheetOpenChange = (isOpen: boolean) => {
    setIsBottomSheetOpen(isOpen);
    if (!isOpen) {
      onClose();
    }
  }

  const addIdea = async () => {
    setAddingInProgress(true);
    try {
      await applyIdea(idea.id);
      if (onWishSaved) {
        await onWishSaved();
      }
      haptic.notification('success');
      setIsBottomSheetOpen(false);
      setAddingInProgress(false);
      onClose();
    } catch (e) {
      haptic.notification('error');
      showSnackbar("❌", "system.unknowError");
    }
  }

  const hideIdea = async () => {
    haptic.notification('warning');
    const isConfirmed = confirm(t('idea.shouldHide'));
    if (!isConfirmed) {
      return;
    }
    try {
      setIsBottomSheetOpen(false);
      setAddingInProgress(false);
      await callHideIdea(idea.id);
      if (onWishSaved) {
        await onWishSaved();
      }
      onClose();
    } catch (e) {
      haptic.notification('error');
      showSnackbar("❌", "system.unknowError");
    }
  }

  useEffect(() => {
    haptic.impact('soft');
  }, []);

  useEffect(() => {
    disableVerticalSwipes.ifAvailable();
    setOnClickMainButton(async () => {
      await addIdea();
    })
    mainButton.setParams({
      isVisible: true,
      isLoaderVisible: addingInProgress,
      isEnabled: !addingInProgress,
      text: "+ " + t('idea.apply'),
    });

    return () => {
      enableVerticalSwipes.ifAvailable();
      mainButton.setParams({
        isVisible: false,
      });
    }
  })

  const openLink = () => {
    if (idea.url) {
      haptic.impact('light');
      openExternalLink(idea.url ? idea.url : "")
      datadogLogs.logger.info('Idea link clicked', { id: idea.id })
    }
  }

  const maxSizeImage = idea.image?.sizes[idea.image?.sizes.length - 1];
  let vertical = false;
  if (maxSizeImage && maxSizeImage.height >= maxSizeImage.width) {
    vertical = true
  }

  return (
    <Modal
      header={<ModalHeader>{idea.title}</ModalHeader>}
      open={isBottomSheetOpen}
      onOpenChange={bottomSheetOpenChange}
      className={"min-h-96"}
      style={{
        backgroundColor: (isDark ? "var(--tgui--section_bg_color)" : "var(--tgui--secondary_bg_color)"),
        maxHeight: "calc(100% - 50px)",
      }}
    >
      <div className={"flex flex-col content-center justify-center items-center gap-3 ml-5 mr-5 "}>
        <div className={"relative"}>
          {maxSizeImage && <div className={"p-3 min-w-20 max-h-56 overflow-hidden flex justify-center items-center relative"}
                                style={{height: maxSizeImage?.height + "px"}}>
            {!isImageLoaded && <Spinner className={"image-loader"} size={"l"}/>}
            <img height={vertical ? maxSizeImage.height : undefined}
                 width={!vertical ? maxSizeImage.width : undefined}
                 className={"object-cover max-h-60 shiny-image max-w-full rounded-lg " + (isImageLoaded ? "visible" : "invisible")}
                 src={maxSizeImage.link}
                 onLoad={() => setIsImageLoaded(true)}
                 alt={idea.title}/>
          </div>}

          {!maxSizeImage && <div className={"p-3"}>
            <Image className={"shiny-image"}
                   fallbackIcon={<TbGift color={"#fff"} size={120}/>}
                   // @ts-expect-error size may have this value
                   size={200}/>
          </div>}

          {idea.priceText && <Badge className={"absolute top-3 right-3 "} large={true}
                                    type={"number"} mode={"critical"}>
            {idea.priceText}
          </Badge>}
        </div>

        {idea.url &&
          <Button before={<TbWorldShare size={25} />}
                  onClick={openLink}
                  stretched={true}
                  mode={"bezeled"}>
            {t('wish.action.openLink')}
          </Button>}

        {idea.description && <Blockquote type="text" className={"description"}>
          {renderWithHtml(idea.description)}
        </Blockquote>}

        <Button before={<BiHide size={25} />}
                onClick={hideIdea}
                stretched={true}
                mode={"plain"}>
          {t('idea.hide')}
        </Button>
      </div>
    </Modal>
  )
}
